import { useState, useEffect, useCallback } from "react";
import { Capacitor } from "@capacitor/core";
import { PushNotifications } from "@capacitor/push-notifications";
import { App as CapacitorApp } from "@capacitor/app";
import { requestPushPermissionsAndRegister } from "@/utils/push-notifications";

const DISMISS_KEY = "stasht_notif_nudge_dismissed_v2";
const DISMISS_COOLDOWN_MS = 3 * 24 * 60 * 60 * 1000; // 3 days

type PermissionState = "prompt" | "prompt-with-rationale" | "denied" | "granted";

const isDismissedRecently = (): boolean => {
  const raw = localStorage.getItem(DISMISS_KEY);
  if (!raw) return false;
  const elapsed = Date.now() - Number(raw);
  if (elapsed >= DISMISS_COOLDOWN_MS) {
    localStorage.removeItem(DISMISS_KEY);
    return false;
  }
  return true;
};

export function useNotificationNudge({ enabled = true }: { enabled?: boolean } = {}) {
  const [permissionState, setPermissionState] = useState<PermissionState | null>(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    if (isDismissedRecently()) {
      setDismissed(true);
      return;
    }

    PushNotifications.checkPermissions()
      .then(result => setPermissionState(result.receive as PermissionState))
      .catch(() => {});
  }, []);

  // Re-check when app resumes (user might have changed settings externally)
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    const listener = CapacitorApp.addListener("appStateChange", async ({ isActive }) => {
      if (!isActive) return;
      try {
        const result = await PushNotifications.checkPermissions();
        setPermissionState(result.receive as PermissionState);
      } catch {}
    });

    return () => { void listener.then(l => l.remove()); };
  }, []);

  const dismiss = useCallback(() => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setDismissed(true);
  }, []);

  const enable = useCallback(async () => {
    if (permissionState === "denied") {
      try {
        const { AppLauncher } = await import("@capacitor/app-launcher");
        await AppLauncher.openUrl({ url: "app-settings:" });
      } catch {
        dismiss();
      }
    } else {
      const result = await requestPushPermissionsAndRegister();
      if (result?.receive === "granted") {
        setPermissionState("granted");
      } else {
        dismiss();
      }
    }
  }, [permissionState, dismiss]);

  const shouldShow =
    enabled &&
    Capacitor.isNativePlatform() &&
    permissionState !== null &&
    permissionState !== "granted" &&
    !dismissed;

  return {
    shouldShow,
    isDenied: permissionState === "denied",
    dismiss,
    enable,
  };
}
